// Português (Portugal) — the platform dictionary. Typed CoreStrings: a missing
// or extra key fails typecheck.

import type { CoreStrings } from './en'

export const corePt: CoreStrings = {
  brand: {
    name: '{brand}',
  },
  legal: {
    agree: 'Ao continuar, aceita os',
    terms: 'Termos de serviço',
    and: 'e a',
    privacy: 'Política de privacidade',
  },
  language: {
    title: 'Idioma',
    hint: 'Menus, datas e números. Só para si — cada colega escolhe o seu.',
    switch: 'Idioma',
    en: 'English',
    pt: 'Português',
  },
  time: {
    justNow: 'agora mesmo',
    minutesAgo: 'há {n} min',
    hoursAgo: 'há {n} h',
    daysAgo: 'há {n} d',
  },
  common: {
    loading: 'A carregar…',
    saving: 'A guardar…',
    allSaved: 'Todas as alterações guardadas',
    saveFailed: 'Não foi possível guardar. A última alteração não ficou registada.',
    save: 'Guardar',
    cancel: 'Cancelar',
    close: 'Fechar',
    delete: 'Eliminar',
    confirmDelete: 'Eliminar definitivamente?',
    retry: 'Tentar de novo',
    search: 'Pesquisar',
    clear: 'Limpar',
    none: 'Nenhum',
    error: 'Algo correu mal. Tente novamente.',
    notFound: 'Não encontrado',
    back: 'Voltar',
    optional: 'opcional',
  },
  auth: {
    signIn: 'Iniciar sessão',
    signInSubtitle: 'Use o email com que se registou.',
    email: 'Email',
    emailPlaceholder: 'voce@empresa.com',
    sendMagicLink: 'Enviar-me um link de acesso',
    magicLinkSent: 'Veja em {email} o link de acesso. Expira dentro de uma hora — se expirar, peça outro.',
    backToSignIn: 'Usar outro email',
    orSeparator: 'ou',
    signInWithGoogle: 'Continuar com o Google',
    signOut: 'Terminar sessão',
    noAccount: 'Ainda não tem conta?',
    viewPlans: 'Ver preços',
    forbidden: 'Não tem acesso a esta página.',
    errors: {
      otp_disabled: 'O acesso por email está desativado neste espaço. Use o Google.',
      over_email_send_rate_limit: 'Demasiados emails de acesso seguidos. Aguarde um minuto e tente de novo.',
      signup_disabled: 'Nenhuma conta usa esse email. Confirme o endereço ou veja os preços para criar uma.',
      validation_failed: 'Isso não parece um endereço de email.',
      default: 'Não foi possível iniciar sessão. Tente novamente.',
    },
    heroTitle: 'Bem-vindo ao {brand}.',
    heroSubline: 'Inicie sessão para continuar onde ficou.',
  },
  pricing: {
    title: 'Experimente o {brand} {n} dias grátis',
    intervalLabel: 'Periodicidade',
    monthly: 'Mensal',
    yearly: 'Anual',
    monthsFree: '{n} meses grátis',
    thenMonthly: 'Depois {price} por mês.',
    thenYearly: 'Depois {price} por ano, cerca de {perMonth} por mês.',
    trialKicker: 'Período experimental',
    trialHeadline: '{n} dias grátis',
    selectPlan: 'Começar os {n} dias grátis',
    submitting: 'A abrir o pagamento seguro…',
    loadError: 'Não foi possível carregar os preços. Atualize a página e tente de novo.',
    checkoutError: 'Não foi possível abrir o pagamento. Tente novamente.',
    notConfigured: 'A faturação ainda não está configurada. Execute `pnpm seed:plan` para sincronizar o catálogo do Stripe.',
    alreadyHaveAccount: 'Já tem conta?',
    whatYouGet: 'O que recebe',
  },
  checkout: {
    successTitle: 'Está tudo pronto',
    successBody: 'O seu espaço de trabalho está a ser criado. Enviámos-lhe um link de acesso por email — ou inicie sessão abaixo.',
    successHint: 'A criação demora normalmente alguns segundos.',
    goToSignIn: 'Ir para o início de sessão',
    cancelTitle: 'Pagamento cancelado',
    cancelBody: 'Nada foi cobrado. Pode escolher um plano de novo quando quiser.',
    backToPricing: 'Voltar aos preços',
  },
  paywall: {
    title: 'A sua subscrição não está ativa',
    body: 'Escolha um plano para voltar ao seu espaço de trabalho. Os seus dados estão intactos.',
    recoverableTitle: 'Há um problema com o seu pagamento',
    recoverableBody: 'O último pagamento não foi concluído. Atualize o cartão e o acesso é reposto assim que o Stripe voltar a tentar.',
    updatePayment: 'Atualizar método de pagamento',
    orStartNew: 'Ou iniciar uma nova subscrição',
    pendingTitle: 'O seu espaço de trabalho ainda não está pronto',
    pendingBody: 'O pagamento não terminou, ou a criação ainda está a decorrer. Se acabou de pagar, aguarde um momento e atualize a página.',
    memberBody: 'Só um administrador do espaço de trabalho pode alterar a faturação. Peça-lhe que atualize a subscrição.',
    signedInAs: 'Sessão iniciada como',
    portalError: 'Não foi possível abrir o portal de faturação. Tente novamente.',
  },
  nav: {
    home: 'Visão geral',
    settings: 'Definições',
  },
  settings: {
    title: 'Definições',
    team: 'Equipa',
    teamHint: 'Pessoas com acesso a este espaço de trabalho.',
    roleAdmin: 'Administrador',
    roleMember: 'Membro',
    inviteHint: 'Adicionar colegas ainda não é self-service — ver docs/OPEN-QUESTIONS.md. Peça ao operador que os adicione.',
    adminOnly: 'Só os administradores podem alterar estas definições.',
    saved: 'Guardado',
    billing: 'Faturação',
    billingHint: 'Faturas, método de pagamento e cancelamento são geridos pelo Stripe.',
    billingPlan: 'Plano',
    billingStatus: 'Estado',
    billingRenews: 'Renova a',
    billingEnds: 'Acesso termina a',
    billingTrialEnds: 'Período experimental termina a',
    billingCancelScheduled: 'Cancelamento agendado',
    billingNone: 'Não há subscrição registada para este espaço de trabalho.',
    manageBilling: 'Gerir faturação',
    openingPortal: 'A abrir o Stripe…',
    portalError: 'Não foi possível abrir o portal de faturação. Tente novamente.',
    subscriptionStatus: {
      active: 'Ativa',
      trialing: 'Período experimental',
      past_due: 'Pagamento em atraso',
      unpaid: 'Por pagar',
      canceled: 'Cancelada',
      incomplete: 'Incompleta',
      unknown: 'Desconhecido',
    },
  },
}
